/**
 * Accepted file types for upload and drag-and-drop operations
 * This configuration is used by FileUploaderV2 and Flow canvas drop handler
 */
export const ACCEPTED_FILE_TYPES = {
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
  'image/heic': ['.heic'],
  'image/webp': ['.webp'],
  'video/webm': ['.webm'],
  'video/mp4': ['.mp4'],
  'video/quicktime': ['.mov'],
  'audio/*': ['.mp3', '.wav', '.ogg', '.m4a'],
  'model/gltf-binary': ['.glb'],
  'model/obj': ['.obj'],
  'model/fbx': ['.fbx'],
  'model/ply': ['.ply'],
};

/** Files only the agent chat accepts, uploaded to Blobstore; mirrored by core's document allowlist. */
export const AGENT_CHAT_DOCUMENT_FILE_TYPES: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'text/plain': ['.txt'],
  'text/markdown': ['.md'],
  'text/csv': ['.csv'],
  'text/tab-separated-values': ['.tsv'],
  'application/json': ['.json'],
};

/** The `accept` list for the agent chat file picker: canvas media plus documents. */
export const AGENT_CHAT_ACCEPTED_FILE_TYPES = { ...ACCEPTED_FILE_TYPES, ...AGENT_CHAT_DOCUMENT_FILE_TYPES };

/** The document MIME type for an agent chat attachment, by extension since browsers report text types inconsistently. */
export const getAgentChatDocumentMimeType = (fileName: string): string | undefined => {
  const extension = getFileExtension(fileName);
  return Object.keys(AGENT_CHAT_DOCUMENT_FILE_TYPES).find(
    (mimeType) => extension && AGENT_CHAT_DOCUMENT_FILE_TYPES[mimeType].includes(extension),
  );
};

/** Image types accepted by the community publish flow (JPG/PNG only). */
export const COMMUNITY_PUBLISH_IMAGE_TYPES = {
  'image/jpeg': ACCEPTED_FILE_TYPES['image/jpeg'],
  'image/png': ACCEPTED_FILE_TYPES['image/png'],
};

/**
 * Check if a file is accepted based on MIME type and extension
 * @param file - The file to check
 * @returns true if the file type is accepted, false otherwise
 */
const fileMatchesAcceptedTypes = (file: File, accepted: Partial<typeof ACCEPTED_FILE_TYPES>): boolean => {
  const acceptedMimeTypes = Object.keys(accepted);
  const mimeTypeMatch = acceptedMimeTypes.some((mimeType) => {
    if (mimeType.endsWith('/*')) {
      const baseType = mimeType.slice(0, -2);
      return file.type.startsWith(`${baseType}/`);
    }
    return file.type === mimeType;
  });
  if (mimeTypeMatch) {
    return true;
  }

  const fileExtension = getFileExtension(file.name);
  if (!fileExtension) {
    return false;
  }
  return acceptedMimeTypes.some((mimeType) => accepted[mimeType as keyof typeof accepted]?.includes(fileExtension));
};

export const isFileTypeAccepted = (file: File): boolean => fileMatchesAcceptedTypes(file, ACCEPTED_FILE_TYPES);

/** Same mime and extension rules as the dropzone `accept` map for this node's category. */
export const fileMatchesAcceptedCategory = (file: File, acceptedFileType?: string): boolean =>
  fileMatchesAcceptedTypes(file, getFilteredAcceptedFileTypes(acceptedFileType));

/**
 * Extract file extension safely, handling edge cases
 * @param fileName - The file name to extract extension from
 * @returns The file extension with leading dot, or null if no extension found
 */
const getFileExtension = (fileName: string): string | null => {
  // Handle empty or invalid file names
  if (!fileName || typeof fileName !== 'string') {
    return null;
  }

  // Find the last dot that's not at the beginning or end
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex <= 0 || lastDotIndex === fileName.length - 1) {
    return null; // No valid extension found
  }

  return '.' + fileName.slice(lastDotIndex + 1).toLowerCase();
};

/**
 * Map of file extensions to MIME types for formats that browsers don't natively recognize.
 * Used to provide the correct MIME type hint when uploading files.
 */
const EXTENSION_MIME_MAP: Record<string, string> = {
  '.heic': 'image/heic',
  '.glb': 'model/gltf-binary',
  '.obj': 'model/obj',
  '.fbx': 'model/fbx',
  '.ply': 'model/ply',
};

export const getMimeTypeFromExtension = (fileName: string): string | undefined => {
  const ext = '.' + fileName.split('.').pop()?.toLowerCase();
  return EXTENSION_MIME_MAP[ext];
};

/**
 * Filter accepted file types based on the specified file type category
 * @param acceptedFileType - The file type category ('image', 'video', 'audio', '3D')
 * @returns Filtered accepted file types configuration
 */
export const getFilteredAcceptedFileTypes = (acceptedFileTypeCategory?: string) => {
  if (!acceptedFileTypeCategory) {
    return ACCEPTED_FILE_TYPES;
  }

  const filteredTypes: Partial<typeof ACCEPTED_FILE_TYPES> = {};

  // Map acceptedFileType to MIME type prefixes
  const typePrefixMap: Record<string, string> = {
    image: 'image',
    video: 'video',
    audio: 'audio',
    '3D': 'model',
  };

  const prefix = typePrefixMap[acceptedFileTypeCategory];
  if (!prefix) {
    // If unknown type, return all accepted types
    return ACCEPTED_FILE_TYPES;
  }

  // Filter file types that match the prefix
  Object.entries(ACCEPTED_FILE_TYPES).forEach(([mimeType, extensions]) => {
    if (mimeType.startsWith(prefix)) {
      filteredTypes[mimeType as keyof typeof ACCEPTED_FILE_TYPES] = extensions;
    }
  });

  return filteredTypes;
};
